export type Family = "ipv4" | "ipv6";

// The whole of what the two families differ in once an address is a number: how many bits there are of it.
export const BITS: Record<Family, number> = { ipv4: 32, ipv6: 128 };

export interface Address {
  family: Family;
  value: bigint;
  // The interface a link-local address is scoped to, which is a name and no part of any arithmetic done with it.
  zone: string;
}

export interface Reading {
  address: Address;
  prefix: number;
}

// Blank is a box nobody has finished with rather than a wrong one, so it is a third answer and not an error.
export type Result =
  | { kind: "blank" }
  | { kind: "reading"; reading: Reading }
  | { kind: "error"; message: string };

// One field takes the address and the width together, because a block is what gets pasted and `/24` is part of it.
export function readCidr(text: string, family: Family): Result {
  const trimmed = text.trim();
  if (!trimmed) return { kind: "blank" };

  const slash = trimmed.indexOf("/");
  const address = parseAddress(slash < 0 ? trimmed : trimmed.slice(0, slash), family);
  if (address === null) return { kind: "error", message: ADDRESS_PROBLEM[family] };

  const prefix = slash < 0 ? BITS[family] : parsePrefix(trimmed.slice(slash + 1).trim(), family);
  if (prefix === null) return { kind: "error", message: `A prefix length is a whole number from 0 to ${BITS[family]}` };

  return { kind: "reading", reading: { address, prefix } };
}

export function parseAddress(text: string, family: Family): Address | null {
  const cut = text.indexOf("%");
  const zone = cut < 0 ? "" : text.slice(cut + 1);
  // A scope names an interface and only IPv6 has them, so a per cent anywhere else is a character and not a zone.
  if (cut >= 0 && (family !== "ipv6" || zone === "")) return null;

  const value = parseValue(cut < 0 ? text : text.slice(0, cut), family);
  return value === null ? null : { family, value, zone };
}

// The address is a number written the way its family writes numbers, so the number itself is read here too: that is
// the other direction of IP to integer, and it is a column somebody is pasting out of a database rather than a form.
function parseValue(text: string, family: Family): bigint | null {
  const whole = parseWhole(text);
  if (whole !== null) return whole < 1n << BigInt(BITS[family]) ? whole : null;
  return family === "ipv4" ? parseIpv4(text) : parseIpv6(text);
}

function parseWhole(text: string): bigint | null {
  if (/^\d+$/.test(text) || /^0[xX][0-9a-fA-F]+$/.test(text)) return BigInt(text);
  return null;
}

function parseIpv4(text: string): bigint | null {
  const octets = text.split(".");
  if (octets.length !== 4) return null;

  let value = 0n;
  for (const octet of octets) {
    if (!/^\d{1,3}$/.test(octet)) return null;
    // 010 is eight to whoever wrote it as octal and ten to whoever did not, so it is read as neither.
    if (octet.length > 1 && octet.startsWith("0")) return null;
    const number = Number(octet);
    if (number > 255) return null;
    value = (value << 8n) | BigInt(number);
  }
  return value;
}

function parseIpv6(text: string): bigint | null {
  const halves = text.split("::");
  if (halves.length > 2) return null;

  // A dotted quad may only ever end the address, which is the last half of it and the whole of it when there is no `::`.
  const head = parseGroups(halves[0], halves.length === 1);
  const tail = halves.length === 2 ? parseGroups(halves[1], true) : [];
  if (head === null || tail === null) return null;

  const written = head.length + tail.length;
  // Without `::` every group is written out; with it there has to be at least one left for the `::` to stand for.
  if (halves.length === 1 ? written !== 8 : written > 7) return null;

  const groups = [...head, ...Array(8 - written).fill(0), ...tail];
  return groups.reduce<bigint>((value, group) => (value << 16n) | BigInt(group), 0n);
}

function parseGroups(half: string, embedded: boolean): number[] | null {
  if (half === "") return [];

  const tokens = half.split(":");
  const groups: number[] = [];
  for (const [index, token] of tokens.entries()) {
    if (embedded && index === tokens.length - 1 && token.includes(".")) {
      const quad = parseIpv4(token);
      if (quad === null) return null;
      groups.push(Number(quad >> 16n), Number(quad & 0xffffn));
      continue;
    }
    if (!/^[0-9a-fA-F]{1,4}$/.test(token)) return null;
    groups.push(parseInt(token, 16));
  }
  return groups;
}

function parsePrefix(text: string, family: Family): number | null {
  if (!/^\d{1,3}$/.test(text)) return null;
  const prefix = Number(text);
  return prefix <= BITS[family] ? prefix : null;
}

// What the width field shows, which for an address written without one is the single address it names.
export function prefixOf(text: string, family: Family): number | null {
  const slash = text.indexOf("/");
  return slash < 0 ? BITS[family] : parsePrefix(text.slice(slash + 1).trim(), family);
}

// The field and the text are one state and not two, so turning the spinner rewrites the suffix on the address.
export function withPrefix(text: string, prefix: number): string {
  const slash = text.indexOf("/");
  return `${(slash < 0 ? text : text.slice(0, slash)).trim()}/${prefix}`;
}

// Which family a text plainly belongs to, so pasting one into the other's mode moves the mode rather than erroring.
// A bare number belongs to neither and leaves the mode where it is, both families being able to hold a small one.
export function familyOf(text: string): Family | null {
  const body = text.split("/")[0].trim();
  if (body.includes(":")) return "ipv6";
  return body.includes(".") ? "ipv4" : null;
}

const ADDRESS_PROBLEM: Record<Family, string> = {
  ipv4: "Four numbers 0 to 255 separated by dots, or a whole number up to 4294967295",
  ipv6: "Up to eight groups of four hex digits, with :: standing in for a run of zeros",
};
